import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { agentApi } from "../../api/client";
import { Button, Badge, Card, Input, Modal, RichTable, TogglePill } from "../../components/ui";

// I² Visual Identity v1 — R2.2 Library landing pilot (2026-04-29).
// Per design doc §6 Ring 2, sign-off Q1 batched commit (R2.1+R2.2).
// Agent tiles: thinGlass + spatial shadow + grain + staggered cascade
// entry (0.05s/tile, backOut easing) + spring hover (scale 1.01, y -4).
import { useWebSocketChat } from "../../hooks/useWebSocketChat";
import PublishTemplateModal from "../marketplace/PublishTemplateModal";
import AgentViewer from "./viewer/AgentViewer";
import Icon from "../../components/Icon";
import PageHeader from "../../components/PageHeader";
import { MarkdownText } from "../../components/MarkdownText";
import { ChatCitations } from "../../components/ChatCitations";
import { SupervisorMessageDecorations } from "../../components/SupervisorMessageDecorations";
// Phase D — iPhone-grid Library (agent-experience-redesign-v1.md §4)
import AgentTileGrid from "./iphone-grid/AgentTileGrid";
import ViewModeToggle, { readViewMode } from "./iphone-grid/ViewModeToggle";
import DensityToggle, { readDensity, writeDensity } from "./iphone-grid/DensityToggle";
import TileHoverPreview from "./iphone-grid/TileHoverPreview";
import TileContextMenu from "./iphone-grid/TileContextMenu";
import LibrarySearchToolbar, { DEFAULT_SEARCH_BAR } from "./iphone-grid/LibrarySearchToolbar";
import AgentAvatar from "./iphone-grid/AgentAvatar";
import { getEffectiveCategory } from "./iphone-grid/agentVisuals";
// Phase B11.1: Shared agent-builder chat panel. Used here AND by the
// Builder canvas (BuilderView) so the two surfaces don't drift.
import AgentBuilderChat from "../agent-chat/AgentBuilderChat";
import { useAuth } from "../../contexts/AuthContext";

// §19 patch K-2 — only developer-tier roles can author agents. The
// backend enforces this via require_developer on PUT /agents/:id/tree
// + POST /agents/:id/test-script + every Super Agent tool. Hiding the
// UI is defense-in-depth + a UX nicety (business_user / supervisor
// shouldn't see a button that 403s on Accept).
const _DEV_ROLES = ["owner", "administrator", "developer"];

// Grid page size for the paginated fetch (agentApi.listPaged). 24 divides
// evenly into the grid's common column counts (4/6/8-wide breakpoints).
const PAGE_SIZE = 24;

const fmtVer = v => v ? `v${v.replace(/^v+/, "")}` : "";

/** Thin wrapper — uses shared RichTable from ui.jsx */
function RichTableElement({ el }) {
  return <RichTable el={el} colors={COLORS} />;
}

// I² Visual Identity v1, R2.2 — Agent tile.
//
// Consistency pass 2026-04-29: per user feedback ("Cards on Agents
// should be consistent like Agent Builder"), tiles use the same solid
// surface convention as Builder's existing Card component (COLORS.surface
// + COLORS.border + spatialShadow). The gradient backdrop + ambient
// orbs at Shell level are the connecting tissue; cards across tabs
// stay visually identical so the eye doesn't read "different products."
//
// Kept from polish: cascade entry stagger + spring hover (subtle
// scale + lift). Dropped: thinGlass material (translucency made cards
// look unrelated to Builder); edge shine; grain (added noise without
// payoff at this opacity).
function AgentTile({ agent, idx, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 25,
        delay: Math.min(idx * 0.05, 0.6),
      }}
      whileHover={{
        scale: 1.01,
        y: -3,
        transition: { type: "spring", stiffness: 400, damping: 25 },
      }}
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: RADIUS.inner,
        boxShadow: MATERIALS.spatialShadow(),
        padding: "20px 22px",
        cursor: "default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {children}
    </motion.div>
  );
}

const AgentLibraryView = ({ onEditAgent, deepLinkAgentId }) => {
  const bp = useBreakpoint();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isDeveloper = _DEV_ROLES.includes(currentUser?.role);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  // 2026-05-24 deep-link fix: URL /library/<uuid> now opens
  // AgentViewer for that agent directly. Previously selectedAgentId
  // was only set by tile clicks, so pasting an agent URL showed the
  // agent list instead of the agent detail. Shell parses the :agentId
  // from location.pathname and passes it as deepLinkAgentId prop —
  // useParams() doesn't work here because App's outer route is
  // path="/*" splat with no named params. Wired through useEffect →
  // setSelectedAgentId so the existing viewer mount logic renders
  // without any other change.
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [agentHistory, setAgentHistory] = useState([]); // stack for Back navigation
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [publishAgent, setPublishAgent] = useState(null);
  const [chatAgent, setChatAgent] = useState(null); // Agent currently being chatted with (Agent Builder)
  const [directChatAgent, setDirectChatAgent] = useState(null); // Direct chat with an agent
  const [learnAgent, setLearnAgent] = useState(null); // Agent being analyzed for learning
  const [learnData, setLearnData] = useState(null);
  const [learnLoading, setLearnLoading] = useState(false);
  const [applyingFewShots, setApplyingFewShots] = useState(false);
  // Phase D — iPhone-grid (default) with list-mode escape hatch.
  // §16.2 #1/2/5: viewMode + density persist to localStorage; default
  // = grid + comfortable; auto-flip to compact when tenant has > 50
  // agents at first load (user can override).
  const [viewMode, setViewMode] = useState(() => readViewMode());
  const [density, setDensity] = useState(() => readDensity());
  // D3 — hover preview state, set by AgentTile after 600ms hover.
  const [hoverPreview, setHoverPreview] = useState(null); // { agent, anchorRect } | null
  // Debounced close — gives the user ~200ms to move the pointer from
  // the tile onto the floating card. Without this, the card vanished
  // the instant the mouse left the tile (gap is ~12px) and the
  // Open / Test buttons couldn't be clicked. The card itself cancels
  // the pending close on mouse-enter and re-schedules on mouse-leave.
  const hoverCloseTimerRef = useRef(null);
  const cancelHoverClose = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };
  const scheduleHoverClose = () => {
    cancelHoverClose();
    hoverCloseTimerRef.current = setTimeout(() => setHoverPreview(null), 200);
  };
  // D8 — right-click context menu state.
  const [contextMenu, setContextMenu] = useState(null);   // { agent, x, y } | null
  // Option C of agents-page filter UX: ONE state object drives search +
  // filters + sort across BOTH grid and list views. Replaces the prior
  // dual-track setup (legacy `search`/`filter` for list view AND
  // `searchBar` for grid view) — single source of truth, no
  // duplicate-toolbar UX.
  const [searchBar, setSearchBar] = useState(DEFAULT_SEARCH_BAR);

  // pageAgents/pageMeta — the SERVER-paginated, SERVER-filtered set that
  // actually renders in the grid/list. Distinct from `agents` below,
  // which stays the full (unpaginated) tenant list and only feeds the
  // toolbar's per-axis counts, the category strip, and the density
  // auto-flip — none of those want "just this page".
  const [pageAgents, setPageAgents] = useState([]);
  const [pageMeta, setPageMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await agentApi.list();
      if (res.success) setAgents(res.data || []);
    } catch {} finally { setLoading(false); }
  };

  // fetchPage(1, {reset:true}) — replace the grid with a fresh page 1
  // (any filter/sort/search change, or an explicit refresh).
  // fetchPage(n) — append page n to what's already showing ("Load more").
  // Experts are always excluded here (include_experts:false) — the
  // Library's contract per applySearchFilter's old expert-hide, now
  // enforced server-side instead of client-side.
  const fetchPage = async (pageNum, { reset = false } = {}) => {
    if (reset) setPageLoading(true); else setLoadingMore(true);
    try {
      const res = await agentApi.listPaged({
        page: pageNum, pageSize: PAGE_SIZE, includeExperts: false,
        search: searchBar.search, status: searchBar.status,
        kind: searchBar.kind, channel: searchBar.channel,
        category: searchBar.category, sort: searchBar.sort,
      });
      if (res.success) {
        setPageAgents(prev => reset ? (res.data || []) : [...prev, ...(res.data || [])]);
        setPageMeta(res.meta || null);
        setPage(pageNum);
      }
    } catch {} finally {
      setPageLoading(false); setLoadingMore(false);
    }
  };

  // Refresh BOTH the full list (toolbar/counts) and the current page
  // (grid) — for actions that add/rename agents outside a normal filter
  // change: bootstrap buttons, returning from the agent viewer, few-shot
  // apply.
  const refreshAll = () => { fetchAgents(); fetchPage(1, { reset: true }); };

  // Sync URL deep-link param → selectedAgentId. Fires on mount and
  // whenever the URL id changes (browser back/forward, pasting a
  // different /library/<id> URL). Setting null in selectedAgentId
  // when URL has no id ensures going from /library/abc → /library
  // closes the viewer.
  useEffect(() => {
    setSelectedAgentId(deepLinkAgentId || null);
  }, [deepLinkAgentId]);

  // Fetch once on mount — no background poll. Freshness after mount
  // comes from explicit refreshAll() calls (delete, status toggle,
  // bootstrap buttons, returning from the agent viewer, few-shot apply)
  // rather than a timer. The only gap this leaves: agent changes made
  // OUTSIDE this tab (another session, a background job) won't appear
  // here until the user navigates away and back, or reloads. Removed
  // 2026-08-02 — every real mutation path already refreshes explicitly,
  // so the 10s poll was mostly background noise for no real freshness
  // gain at this tenant's scale.
  useEffect(() => {
    fetchAgents();
  }, []);

  // Any filter/search/sort change → reset to page 1 server-side. Keyed
  // on a serialized searchBar (not the object reference) so this fires
  // exactly once per actual value change, including the initial mount.
  const searchBarKey = JSON.stringify(searchBar);
  useEffect(() => {
    fetchPage(1, { reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBarKey]);

  // visibleAgents — the post-expert-hide universe used for tallies,
  // category counts, and density auto-toggle.
  // Mirrors the include_experts:false the grid fetch (fetchPage) sends
  // server-side, so every count surface stays consistent with what's
  // actually visible in the grid. Experts are hidden via
  // metadata_json.is_use_case_expert (see backend list_agents +
  // LibrarySearchToolbar.jsx's counts).
  const visibleAgents = useMemo(
    () => agents.filter(a => !(a?.metadata_json?.is_use_case_expert === true)),
    [agents],
  );

  // Phase D / §16.2 #2 — auto-flip density to compact when tenant has
  // > 50 agents on first load. Skipped if the user has already
  // explicitly set a density preference (localStorage value present).
  useEffect(() => {
    if (loading) return;
    let userOverridden = false;
    try { userOverridden = !!localStorage.getItem("library.density"); } catch {}
    if (!userOverridden && visibleAgents.length > 50 && density === "comfortable") {
      setDensity("compact");
      writeDensity("compact");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleAgents.length, loading]);

  const handleDelete = async (id) => {
    if (!confirm("Delete this agent?")) return;
    setDeletingId(id);
    try {
      await agentApi.delete(id);
      setAgents(prev => prev.filter(a => a.id !== id));
      // Removing a row can never violate the active filter, so patching
      // pageAgents in place is always correct — no refetch needed.
      setPageAgents(prev => prev.filter(a => a.id !== id));
      setPageMeta(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev);
    } catch {} finally { setDeletingId(null); }
  };

  const handleSetStatus = async (agent, newStatus) => {
    if (agent.status === newStatus) return;
    setTogglingId(agent.id);
    try {
      const res = await agentApi.updateStatus(agent.id, newStatus);
      if (res.success) {
        setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, status: newStatus } : a));
        // A status change can only ever remove a row from the CURRENT
        // page — it was already on this page, so it already satisfied
        // every other active filter; only an active status filter that
        // no longer matches can invalidate it. Patch in place otherwise.
        const statusFilterActive = searchBar.status && searchBar.status !== "all";
        if (statusFilterActive && newStatus !== searchBar.status) {
          setPageAgents(prev => prev.filter(a => a.id !== agent.id));
          setPageMeta(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev);
        } else {
          setPageAgents(prev => prev.map(a => a.id === agent.id ? { ...a, status: newStatus } : a));
        }
      }
    } catch {} finally { setTogglingId(null); }
  };

  const statusColor = { live: COLORS.green, paused: COLORS.orange, draft: COLORS.textDim };
  const statusIconName = { live: "status.live", paused: "status.paused", draft: "status.draft" };

  // Option C: the toolbar state is the only source of truth for search +
  // filter + sort; legacy dual-state (search/filter strings AND
  // searchBar) was removed. `pageAgents` — set by fetchPage() above — IS
  // the filtered+sorted+paginated result; both grid and list views read
  // it directly instead of re-deriving it client-side.

  if (selectedAgentId) {
    return (
      <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>
        <AgentViewer
          agentId={selectedAgentId}
          onBack={() => {
            if (agentHistory.length > 0) {
              // Return to the previous agent (e.g. supervisor after viewing specialist).
              const prev = agentHistory[agentHistory.length - 1];
              setAgentHistory(h => h.slice(0, -1));
              setSelectedAgentId(prev);
              navigate(`/library/${prev}`);
            } else {
              // Clear URL deep-link param so refresh doesn't snap back to viewer.
              setSelectedAgentId(null);
              setAgentHistory([]);
              navigate("/library");
              refreshAll();
            }
          }}
          onEditAgent={onEditAgent ? (agent) => { setSelectedAgentId(null); setAgentHistory([]); onEditAgent(agent); } : undefined}
          onSelectAgent={(id) => {
            // Push current agent onto history stack so Back returns here.
            setAgentHistory(h => [...h, selectedAgentId]);
            setSelectedAgentId(id);
            navigate(`/library/${id}`);
          }}
        />
      </div>
    );
  }

  return (
    <div className="panel-scroll" style={{
      flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px",
      overflowY: "auto",
      // Background + ambient orbs now live at Shell level for unified
      // backplane across every tab (moderation pass).
    }}>
      <PageHeader
        title="Agents"
        primaryAction={isDeveloper ? {
          label: "Create with AI",
          iconName: "feature.spark",
          onClick: () => setChatAgent({ id: null, name: "New Agent", provider: "", model: "", status: "new", channels: [] }),
          style: { background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`, border: "none", color: "#fff" },
        } : null}
        secondaryActions={[
          {
            label: "Platform Tester",
            iconName: "feature.tester",
            onClick: async () => {
              try {
                const res = await agentApi.bootstrapPlatformTester();
                if (res.success) refreshAll();
              } catch {}
            },
            style: { background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.cyan})`, border: "none", color: "#fff" },
          },
          {
            label: "Orchestrator",
            iconName: "tab.workflow",
            onClick: async () => {
              try {
                const res = await agentApi.bootstrapOrchestrator();
                if (res.success) refreshAll();
              } catch {}
            },
            style: { background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.pink})`, border: "none", color: "#fff" },
          },
          // Data Analyst moved to Marketplace per UX feedback —
          // bootstrapping a heavy analytics agent from a button on the
          // Library was confusing alongside "Create with AI". Users
          // can now install it from Marketplace like any other
          // template (the bootstrap endpoint is wired through the
          // platform_data_analyst use-case).
        ]}
      />

      {/* Option C toolbar — stats-strip leading slot on the left, search
          + Filters popover + sort + view-mode/density flush right. */}
      <div style={{ marginBottom: 16 }}>
        <LibrarySearchToolbar
          value={searchBar}
          onChange={setSearchBar}
          agents={visibleAgents}
          leadingSlot={
            <CategoryStrip
              agents={visibleAgents}
              activeCategory={searchBar?.category || ""}
              onSelect={(category) => setSearchBar({ ...searchBar, category })}
            />
          }
          toolbarTrailing={
            <>
              {viewMode === "grid" && (
                <DensityToggle density={density} onChange={setDensity} />
              )}
              <ViewModeToggle mode={viewMode} onChange={setViewMode} />
            </>
          }
        />
      </div>

      {/* Has the user narrowed by ANY filter chip / search? Used to
          show a "Clear filters" CTA on empty state — without it, a
          channel/status chip with no matching agents left the user on
          a blank page with no escape. */}
      {(() => null)()}
      {pageLoading ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.textMuted, fontSize: 13 }}>Loading agents...</div>
      ) : viewMode === "grid" ? (
        pageAgents.length === 0 ? (
          <EmptyAgentsState
            hasFilters={!!(searchBar?.search || (searchBar?.status && searchBar.status !== "all") || (searchBar?.kind && searchBar.kind !== "all") || searchBar?.channel)}
            activeChannel={searchBar?.channel}
            activeStatus={searchBar?.status && searchBar.status !== "all" ? searchBar.status : null}
            onClear={() => setSearchBar(DEFAULT_SEARCH_BAR)}
          />
        ) : (
          <AgentTileGrid
            agents={pageAgents}
            density={density}
            onTileClick={(a) => { setSelectedAgentId(a.id); navigate(`/library/${a.id}`); }}
            onTileContextMenu={(a, pt) => setContextMenu({ agent: a, x: pt.x, y: pt.y })}
            onTileHover={(a, anchorRect) => { cancelHoverClose(); setHoverPreview({ agent: a, anchorRect }); }}
            onTileHoverEnd={scheduleHoverClose}
          />
        )
      ) : pageAgents.length === 0 ? (
        <EmptyAgentsState
          hasFilters={!!(searchBar?.search || (searchBar?.status && searchBar.status !== "all") || (searchBar?.kind && searchBar.kind !== "all") || searchBar?.channel)}
          activeChannel={searchBar?.channel}
          activeStatus={searchBar?.status && searchBar.status !== "all" ? searchBar.status : null}
          onClear={() => setSearchBar(DEFAULT_SEARCH_BAR)}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(auto-fill, minmax(340, 1fr))", gap: 14 }}>
          {pageAgents.map((agent, idx) => (
            <AgentTile key={agent.id} agent={agent} idx={idx}>
              <div style={{ position: "relative", zIndex: 1 }}>
              {/* Header row */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div onClick={() => setSelectedAgentId(agent.id)} style={{ fontSize: 15, fontWeight: 700, color: COLORS.accentText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }} title="View agent details">
                    {agent.name}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{fmtVer(agent.version) || "v1.0"}</div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {agent.metadata_json?.is_self_assembling && (
                    <Badge color={COLORS.pink}>Self-Assembling</Badge>
                  )}
                  {agent.metadata_json?.is_dynamic_agent && !agent.metadata_json?.is_self_assembling && (
                    <Badge color="#8b5cf6">Dynamic</Badge>
                  )}
                  {/* D4 — Voice disclosure off badge.
                      Surfaces when an agent has voice config but the
                      auto-disclosure toggle is explicitly false. Helps
                      compliance reviewers spot agents that may be
                      legally risky in two-party-consent jurisdictions. */}
                  {agent.flow_config?.voice?.voice_id &&
                    agent.flow_config.voice.outbound_disclosure_enabled === false && (
                    <Badge color={COLORS.orange} title="Auto-disclosure is off for outbound calls">
                      No-disclosure
                    </Badge>
                  )}
                  <Badge color={statusColor[agent.status]} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Icon name={statusIconName[agent.status] || "status.draft"} size={11}
                      color={statusColor[agent.status]} />
                    {agent.status}
                  </Badge>
                </div>
              </div>

              {/* Attributes */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 2 }}>Model</div>
                  <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{agent.model || "(not configured)"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 2 }}>Temperature</div>
                  <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{agent.temperature ?? 0.7}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 2 }}>Max Tokens</div>
                  <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>{agent.max_tokens || 2048}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 2 }}>Channels</div>
                  <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>
                    {(agent.channels || []).length > 0 ? agent.channels.join(", ") : "None"}
                  </div>
                </div>
              </div>

              {/* System prompt preview */}
              {agent.system_prompt && (
                <div style={{
                  padding: "8px 10px", borderRadius: 8, background: COLORS.surface,
                  border: `1px solid ${COLORS.border}`, marginBottom: 14,
                }}>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>System Prompt</div>
                  <div style={{
                    fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4,
                    overflow: "hidden", textOverflow: "ellipsis",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>{agent.system_prompt}</div>
                </div>
              )}

              {/* Footer */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 10, color: COLORS.textDim }}>
                  {agent.created_at ? new Date(agent.created_at).toLocaleDateString() : "—"}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {["live", "paused", "draft"].map(s => (
                    <Button key={s} variant="secondary" size="sm"
                      onClick={() => handleSetStatus(agent, s)} disabled={togglingId === agent.id}
                      style={{
                        padding: "4px 9px",
                        background: agent.status === s ? `${statusColor[s]}15` : "transparent",
                        border: `1px solid ${agent.status === s ? statusColor[s] + "40" : COLORS.border}`,
                        color: agent.status === s ? statusColor[s] : COLORS.textDim,
                        textTransform: "capitalize",
                      }}>{s === "live" ? "● " : ""}{s}</Button>
                  ))}
                  <Button variant="secondary" size="sm" onClick={() => setPublishAgent(agent)}
                    style={{
                      // White text on colored-tint chip — same pattern
                      // as Pipeline mode pill + GitHub Copilot Autofix
                      // cards. Purple stays in border + bg as a visual
                      // identifier without sacrificing legibility.
                      border: `1px solid ${COLORS.purple}66`,
                      background: `${COLORS.purple}25`,
                      color: COLORS.text, fontWeight: 600,
                    }}>
                    Publish
                  </Button>
                  {agent.status === "live" && (agent.channels || []).includes("chat") && (
                    <Button variant="secondary" size="sm" onClick={() => setDirectChatAgent(agent)}
                      style={{
                        border: `1px solid ${COLORS.accent}66`,
                        background: `${COLORS.accent}25`,
                        color: COLORS.text, fontWeight: 600,
                      }}>
                      {"\uD83D\uDCAC"} Chat
                    </Button>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => setChatAgent(agent)}
                    style={{ border: `1px solid ${COLORS.green}40`, background: `${COLORS.green}10`, color: COLORS.green }}>
                    {"\uD83E\uDD16"} Tune
                  </Button>
                  <Button variant="secondary" size="sm" onClick={async () => {
                    setLearnAgent(agent); setLearnLoading(true); setLearnData(null);
                    try {
                      const res = await agentApi.analyzeAgent(agent.id);
                      if (res.success) setLearnData(res.data);
                    } catch {} finally { setLearnLoading(false); }
                  }} style={{ border: `1px solid ${COLORS.cyan}40`, background: `${COLORS.cyan}10`, color: COLORS.cyan }}>
                    {"\uD83E\uDDE0"} Learn
                  </Button>
                  {onEditAgent && (
                    <Button variant="secondary" size="sm" onClick={() => onEditAgent(agent)}
                      style={{ border: `1px solid ${COLORS.accent}40`, background: COLORS.accentSoft, color: COLORS.accentText }}>
                      Edit
                    </Button>
                  )}
                  <Button variant="danger" size="sm" onClick={() => handleDelete(agent.id)} disabled={deletingId === agent.id}>
                    {deletingId === agent.id ? "..." : "Delete"}
                  </Button>
                </div>
              </div>
              </div>
            </AgentTile>
          ))}
        </div>
      )}

      {/* Load more — server-paginated footer. Only appears once the
          filtered set exceeds PAGE_SIZE; hidden entirely for the common
          small-tenant case (nothing to load beyond page 1). */}
      {!pageLoading && pageMeta?.has_more && (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button
            variant="secondary"
            onClick={() => fetchPage(page + 1)}
            disabled={loadingMore}
            style={{ padding: "10px 22px" }}
          >
            {loadingMore
              ? "Loading…"
              : `Load more (${pageMeta.total - pageAgents.length} remaining)`}
          </Button>
        </div>
      )}

      {/* Publish Template Modal */}
      {publishAgent && (
        <PublishTemplateModal
          agent={publishAgent}
          onClose={() => setPublishAgent(null)}
          onPublished={() => setPublishAgent(null)}
        />
      )}

      {/* Learn Modal */}
      <LearnModal
        agent={learnAgent}
        data={learnData}
        loading={learnLoading}
        onClose={() => { setLearnAgent(null); setLearnData(null); }}
        applyingFewShots={applyingFewShots}
        onApplyFewShots={async () => {
          if (!learnAgent) return;
          setApplyingFewShots(true);
          try {
            await agentApi.applyFewShots(learnAgent.id);
            refreshAll();
          } catch {} finally { setApplyingFewShots(false); }
        }}
      />

      {/* Agent Chat Panel (Agent Builder) */}
      {chatAgent && (
        <AgentBuilderChat agent={chatAgent} agents={agents} onClose={() => { setChatAgent(null); refreshAll(); }} />
      )}

      {/* Direct Chat — same shared AgentBuilderChat component the
          Builder canvas uses, in directMode so the WS routes messages
          to the agent itself instead of the Super Agent. Same UX
          (default-floating, drag/resize, top-clear, progressive
          markdown streaming) as the AI assist chat in Builder. */}
      {directChatAgent && (
        <AgentBuilderChat
          agent={directChatAgent}
          agents={agents}
          onClose={() => setDirectChatAgent(null)}
          directMode
        />
      )}

      {/* D3 — hover preview floating glass card. The card cancels the
          pending hover-close on enter (so the user can click Open/Test)
          and reschedules on leave. */}
      {hoverPreview && (
        <TileHoverPreview
          agent={hoverPreview.agent}
          anchorRect={hoverPreview.anchorRect}
          onClose={() => { cancelHoverClose(); setHoverPreview(null); }}
          onCardEnter={cancelHoverClose}
          onCardLeave={scheduleHoverClose}
          onOpen={(a) => { cancelHoverClose(); setHoverPreview(null); setSelectedAgentId(a.id); navigate(`/library/${a.id}`); }}
          onTest={(a) => { cancelHoverClose(); setHoverPreview(null); setDirectChatAgent(a); }}
        />
      )}

      {/* D8 — right-click / kebab context menu */}
      {contextMenu && (
        <TileContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            { key: "open",  label: "Open",
              onClick: () => setSelectedAgentId(contextMenu.agent.id) },
            { key: "test",  label: "Test in chat",
              onClick: () => setDirectChatAgent(contextMenu.agent) },
            { key: "edit",  label: "Edit in Builder",
              onClick: () => onEditAgent && onEditAgent(contextMenu.agent),
              disabled: !onEditAgent },
            { divider: true },
            { key: "learn", label: "Learn & improve",
              onClick: () => { setLearnAgent(contextMenu.agent); setLearnLoading(true); setLearnData(null); } },
            { key: "publish", label: "Publish to marketplace",
              onClick: () => setPublishAgent(contextMenu.agent) },
            { divider: true },
            { key: "pause",   label: contextMenu.agent.status === "paused" ? "Resume" : "Pause",
              onClick: () => handleSetStatus(contextMenu.agent, contextMenu.agent.status === "paused" ? "live" : "paused") },
            { key: "delete",  label: "Delete", danger: true,
              onClick: () => handleDelete(contextMenu.agent.id) },
          ]}
        />
      )}
    </div>
  );
};





// ─── Learn Modal (rendered inside AgentLibraryView) ───────
function LearnModal({ agent, data, loading, onClose, onApplyFewShots, applyingFewShots }) {
  const [activeTab, setActiveTab] = useState("learn");
  const [accuracy, setAccuracy] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [ftHistory, setFtHistory] = useState(null);
  const [ftExport, setFtExport] = useState(null);
  const [ftLoading, setFtLoading] = useState(false);
  const [applyingIdx, setApplyingIdx] = useState(null);
  const [appliedSet, setAppliedSet] = useState(new Set());

  useEffect(() => {
    if (agent && activeTab === "learn") {
      agentApi.getAccuracyStats(agent.id, 30).then(r => r.success && setAccuracy(r.data)).catch(() => {});
      agentApi.getLearningSuggestions(agent.id, 7).then(r => r.success && setSuggestions(r.data)).catch(() => {});
    }
    if (agent && activeTab === "finetune") {
      agentApi.getFineTuneHistory(agent.id).then(r => r.success && setFtHistory(r.data)).catch(() => {});
    }
  }, [agent, activeTab]);

  if (!agent) return null;

  const tabStyle = (t) => ({
    padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, borderRadius: "8px 8px 0 0",
    background: activeTab === t ? COLORS.surface : "transparent",
    color: activeTab === t ? COLORS.accent : COLORS.textMuted,
    border: activeTab === t ? `1px solid ${COLORS.border}` : "1px solid transparent",
    borderBottom: activeTab === t ? `1px solid ${COLORS.surface}` : "none",
  });

  return (
    <Modal open={!!agent} title={`Learn & Improve — ${agent.name}`} onClose={onClose} width={650}>
      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${COLORS.border}`, marginBottom: 12 }}>
        <div style={tabStyle("learn")} onClick={() => setActiveTab("learn")}>Learn</div>
        <div style={tabStyle("finetune")} onClick={() => setActiveTab("finetune")}>Fine-Tune</div>
      </div>

      {activeTab === "finetune" && (
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Accuracy / readiness */}
          {accuracy && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
              {[
                { label: "Verified Responses", value: accuracy.total_verified },
                { label: "Accuracy", value: `${accuracy.accuracy_rate}%`, color: accuracy.accuracy_rate >= 90 ? COLORS.green : COLORS.pink },
                { label: "Trainable", value: accuracy.trainable_responses, color: accuracy.fine_tune_ready ? COLORS.green : COLORS.textMuted },
              ].map(s => (
                <div key={s.label} style={{ padding: 10, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color || COLORS.text }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
          {accuracy?.fine_tune_ready ? (
            <div style={{ padding: 10, borderRadius: 8, background: `${COLORS.green}10`, border: `1px solid ${COLORS.green}30`, marginBottom: 12, fontSize: 12, color: COLORS.green }}>
              Ready for fine-tuning — {accuracy.trainable_responses} verified responses available
            </div>
          ) : accuracy ? (
            <div style={{ padding: 10, borderRadius: 8, background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}30`, marginBottom: 12, fontSize: 12, color: COLORS.pink }}>
              Need {1000 - (accuracy.trainable_responses || 0)} more verified ACCURATE responses (minimum: 1,000)
            </div>
          ) : null}

          {/* Export preview */}
          <div style={{ marginBottom: 14 }}>
            <Button size="sm" onClick={async () => {
              setFtLoading(true);
              try { const r = await agentApi.exportTrainingData(agent.id); if (r.success) setFtExport(r.data); } catch {} finally { setFtLoading(false); }
            }} disabled={ftLoading} style={{ background: `${COLORS.accent}15`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30`, marginBottom: 8 }}>
              {ftLoading ? "Exporting..." : "Preview Training Data"}
            </Button>
            {ftExport && (
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>
                {ftExport.count} training examples ready
                {ftExport.preview?.slice(0, 2).map((p, i) => (
                  <div key={i} style={{ padding: 6, marginTop: 6, borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 10, fontFamily: "monospace" }}>
                    <div style={{ color: COLORS.cyan }}>User: {p.messages?.[1]?.content?.slice(0, 80)}...</div>
                    <div style={{ color: COLORS.green }}>Agent: {p.messages?.[2]?.content?.slice(0, 80)}...</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Start fine-tuning */}
          <Button size="sm" onClick={async () => {
            setFtLoading(true);
            try { await agentApi.startFineTuning(agent.id); agentApi.getFineTuneHistory(agent.id).then(r => r.success && setFtHistory(r.data)); } catch {} finally { setFtLoading(false); }
          }} disabled={ftLoading || !accuracy?.fine_tune_ready}
            style={{ background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`, color: "#fff", border: "none", marginBottom: 14, opacity: accuracy?.fine_tune_ready ? 1 : 0.4 }}>
            Start Fine-Tuning
          </Button>

          {/* History */}
          {ftHistory?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>Fine-Tuning History</div>
              {ftHistory.map(run => (
                <div key={run.id} style={{ padding: 8, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, marginBottom: 6, fontSize: 11 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: COLORS.text, fontWeight: 600 }}>{run.provider}/{run.base_model}</span>
                    <Badge color={run.status === "completed" ? COLORS.green : run.status === "deployed" ? COLORS.cyan : run.status === "failed" ? COLORS.pink : COLORS.textMuted}>{run.status}</Badge>
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 4 }}>
                    {run.training_examples} examples | Score: {run.ground_truth_score ?? "—"}% | {run.created_at?.slice(0, 10)}
                  </div>
                  {run.status === "completed" && !run.ground_truth_score && (
                    <Button size="sm" onClick={() => agentApi.evaluateGroundTruth(agent.id)} style={{ marginTop: 6, fontSize: 10 }}>Run Evaluation</Button>
                  )}
                  {run.status === "completed" && run.ground_truth_score >= 90 && (
                    <Button size="sm" onClick={() => agentApi.deployFineTuned(agent.id, run.id)} style={{ marginTop: 6, fontSize: 10, background: COLORS.green, color: "#fff", border: "none" }}>Deploy</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "learn" && (loading ? (
        <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim }}>
          Analyzing conversations and generating suggestions...
        </div>
      ) : !data ? (
        <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim }}>No analysis available</div>
      ) : data.error ? (
        <div style={{ padding: 20, color: COLORS.pink }}>{data.error}</div>
      ) : (
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Fact Checker accuracy stats */}
          {accuracy && accuracy.total_verified > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
              {[
                { label: "Accuracy", value: `${accuracy.accuracy_rate}%`, color: accuracy.accuracy_rate >= 90 ? COLORS.green : COLORS.pink },
                { label: "Avg Reward", value: accuracy.avg_reward ?? "—" },
                { label: "Disclaimers", value: accuracy.disclaimer_count },
                { label: "Re-prompts", value: accuracy.re_prompt_count },
              ].map(s => (
                <div key={s.label} style={{ padding: 8, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: COLORS.textDim }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: s.color || COLORS.text }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Daily accuracy trend chart */}
          {accuracy?.daily_trend?.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Accuracy Trend (daily)</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 60 }}>
                {accuracy.daily_trend.map((d, i) => {
                  const maxTotal = Math.max(...accuracy.daily_trend.map(x => x.total));
                  const h = maxTotal > 0 ? (d.total / maxTotal) * 50 + 10 : 10;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ width: "100%", height: h, borderRadius: 3, background: `linear-gradient(to top, ${COLORS.green}${Math.round(d.accuracy_pct * 2.55).toString(16).padStart(2, "0")}, ${COLORS.green}40)` }}
                        title={`${d.date}: ${d.accuracy_pct}% accuracy (${d.total} responses)`} />
                      <div style={{ fontSize: 8, color: COLORS.textDim, marginTop: 2 }}>{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Fact Checker suggestions */}
          {suggestions?.length > 0 && suggestions[0].type !== "info" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.accentText, marginBottom: 6 }}>Fact Checker Suggestions</div>
              {suggestions.map((s, i) => (
                <div key={i} style={{ padding: 8, borderRadius: 8, marginBottom: 6, background: COLORS.surface, border: `1px solid ${s.severity === "high" ? COLORS.pink : COLORS.border}30` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                    <Badge color={s.severity === "high" ? COLORS.pink : s.severity === "medium" ? COLORS.accent : COLORS.textMuted}>{s.type}</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.text, marginBottom: 2 }}>{s.message}</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.reason}</div>
                </div>
              ))}
            </div>
          )}

          {/* Performance summary */}
          {data.performance && (
            <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 640 ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
              {[
                { label: "Conversations", value: data.performance.total_conversations },
                { label: "Resolution", value: `${data.performance.resolution_rate}%`, color: COLORS.green },
                { label: "Escalation", value: `${data.performance.escalation_rate}%`, color: COLORS.pink },
                { label: "CSAT", value: data.performance.avg_csat != null ? `${data.performance.avg_csat}/5` : "—" },
              ].map(s => (
                <div key={s.label} style={{ padding: 10, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: s.color || COLORS.text }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Strengths */}
          {data.strengths?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.green, marginBottom: 6 }}>Strengths</div>
              {data.strengths.map((s, i) => (
                <div key={i} style={{ fontSize: 12, color: COLORS.text, marginBottom: 4, paddingLeft: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="status.ok" size={12} color={COLORS.green} /> {s}
                </div>
              ))}
            </div>
          )}

          {/* Weaknesses */}
          {data.weaknesses?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.pink, marginBottom: 6 }}>Areas for Improvement</div>
              {data.weaknesses.map((w, i) => (
                <div key={i} style={{ fontSize: 12, color: COLORS.text, marginBottom: 4, paddingLeft: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="status.warning" size={12} color={COLORS.orange} /> {w}
                </div>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {data.prompt_suggestions?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.accentText, marginBottom: 6 }}>Prompt Suggestions</div>
              {data.prompt_suggestions.map((s, i) => (
                <div key={i} style={{
                  padding: 10, borderRadius: 8, marginBottom: 6,
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <Badge color={s.type === "add" ? COLORS.green : s.type === "remove" ? COLORS.pink : COLORS.accent}>{s.type}</Badge>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>{s.section}</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.text, marginBottom: 4 }}>{s.suggested}</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.reason}</div>
                  <Button size="sm" disabled={applyingIdx === i || appliedSet.has(i)} onClick={async () => {
                    setApplyingIdx(i);
                    try {
                      await agentApi.applySuggestion(agent.id, s);
                      setAppliedSet(prev => new Set([...prev, i]));
                    } catch (err) {
                      console.error("Failed to apply suggestion:", err);
                    } finally { setApplyingIdx(null); }
                  }} style={{ marginTop: 6, background: appliedSet.has(i) ? `${COLORS.green}15` : `${COLORS.accent}15`, color: appliedSet.has(i) ? COLORS.green : COLORS.accent, border: `1px solid ${appliedSet.has(i) ? COLORS.green : COLORS.accent}30` }}>
                    {applyingIdx === i ? "Applying..." : appliedSet.has(i) ? "\u2713 Applied" : "Apply"}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Few-shot auto-apply */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.cyan, marginBottom: 6 }}>Auto-Generate Few-Shot Examples</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>
              Extract the best conversation exchanges and add them to the agent's system prompt as examples.
            </div>
            <Button onClick={onApplyFewShots} disabled={applyingFewShots}
              style={{ background: `${COLORS.cyan}15`, color: COLORS.cyan, border: `1px solid ${COLORS.cyan}30` }}>
              {applyingFewShots ? "Extracting..." : "Extract & Apply Few-Shots"}
            </Button>
          </div>

          {/* Estimated improvement */}
          {data.estimated_improvement && (
            <div style={{
              padding: 12, borderRadius: 8, background: `${COLORS.accent}08`,
              border: `1px solid ${COLORS.accent}18`, fontSize: 12, color: COLORS.textMuted,
            }}>
              <strong>Expected impact:</strong> {data.estimated_improvement}
            </div>
          )}
        </div>
      ))}
    </Modal>
  );
}

/**
 * Empty-state card with a recovery CTA. Was previously a bare "No
 * agents found" message that left users stuck — clicking a channel
 * filter chip with no matching agents replaced the entire grid AND
 * the search bar (since the search bar was inside the grid branch),
 * giving the user no way back.
 *
 * Now: search bar stays mounted, this card explains WHY the result
 * is empty (which filter / search narrowed it), and a "Clear filters"
 * button restores the full list.
 */
function EmptyAgentsState({ hasFilters, activeChannel, activeStatus, onClear }) {
  const reason = hasFilters
    ? (activeChannel
        ? `No agents have the '${activeChannel}' channel enabled.`
        : activeStatus
          ? `No agents match status '${activeStatus}'.`
          : "No agents match the current filters.")
    : "No agents in this tenant yet.";
  const hint = hasFilters
    ? "Try a different filter or clear them all to see every agent."
    : "Create one with 'Create with AI' or install a template from the Marketplace.";
  return (
    <div style={{
      textAlign: "center",
      padding: "60px 24px",
      color: COLORS.textMuted,
      fontSize: 13,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
    }}>
      <div style={{ fontSize: 32, opacity: 0.5 }}>{"\uD83D\uDD0D"}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{reason}</div>
      <div style={{ fontSize: 12, color: COLORS.textDim, maxWidth: 360, lineHeight: 1.5 }}>{hint}</div>
      {hasFilters && (
        <Button onClick={onClear} variant="primary" size="sm"
          style={{ background: COLORS.accent, color: "#fff", marginTop: 4 }}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

// ─── CategoryStrip ────────────────────────────────────────────────
//
// User-defined category filter pills shown to the LEFT of the toolbar.
// Categories are surfaced dynamically from any agent that carries a
// `metadata_json.category` value — admins create categories by typing
// one into an agent's Identity card. Clicking a pill filters the grid
// to that category; an "All" pill resets.
//
// Lifecycle status (live/paused/draft) intentionally NOT here — that
// lives in the Filters popover. Status is platform behavior, category
// is org taxonomy; mixing them on the front strip confused users.
//
// Empty state: when no agent has a category yet, the strip surfaces a
// quiet "+ Add categories" hint that links to the OverviewTab Identity
// card so admins know where to set them.
// Exported for unit testing: Library category-chip counts. Stable alphabetical
// order, but float "System" (platform-managed agents) to the end so user-defined
// categories lead. Extracted from CategoryStrip's useMemo (repo pattern).
export function buildCategoryCounts(agents) {
  const counts = new Map();
  for (const a of (agents || [])) {
    const c = getEffectiveCategory(a);
    if (!c) continue;
    counts.set(c, (counts.get(c) || 0) + 1);
  }
  const entries = [...counts.entries()].map(([name, count]) => ({ name, count }));
  entries.sort((a, b) => {
    if (a.name === "System") return 1;
    if (b.name === "System") return -1;
    return a.name.localeCompare(b.name);
  });
  return entries;
}

function CategoryStrip({ agents, activeCategory, onSelect }) {
  const categories = useMemo(() => buildCategoryCounts(agents), [agents]);

  // Quiet hint when no categories exist yet — surfaces the affordance
  // without occupying visual real estate the way a CTA would.
  if (categories.length === 0) {
    return (
      <span style={{
        fontSize: 11, color: COLORS.textDim,
        padding: "6px 10px",
      }} title="Set a category in any agent's Identity card to start grouping">
        Tip: set a category in an agent's Identity card to group them here.
      </span>
    );
  }

  return (
    <>
      <TogglePill
        active={!activeCategory}
        onClick={() => onSelect("")}
        count={agents.length}
      >
        All
      </TogglePill>
      {categories.map(c => (
        <TogglePill
          key={c.name}
          active={activeCategory === c.name}
          onClick={() => onSelect(c.name)}
          title={c.name}
          count={c.count}
        >
          {c.name}
        </TogglePill>
      ))}
    </>
  );
}


// ─── RecentlyUsedRow ──────────────────────────────────────────────
//
// Horizontal strip of the 5 most-recently-touched agents above the
// main grid. Surface like Linear's "Recent" section + Notion's
// recently-edited row. Reduces clicks for daily-driver agents.
function RecentlyUsedRow({ agents, onSelect }) {
  const recent = useMemo(() => {
    return [...agents]
      .filter(a => a.last_used_at || a.updated_at)
      .sort((a, b) => {
        const ta = a.last_used_at || a.updated_at || "";
        const tb = b.last_used_at || b.updated_at || "";
        return tb.localeCompare(ta);
      })
      .slice(0, 5);
  }, [agents]);

  if (recent.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.08em",
        marginBottom: 10,
      }}>Recently used</div>
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap",
      }}>
        {recent.map(a => (
          <button
            key={a.id}
            type="button"
            onClick={() => onSelect(a)}
            title={a.name}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 14px 8px 8px",
              ...MATERIALS.thinGlass(),
              borderRadius: 999,
              cursor: "pointer",
              transition: "transform 0.12s, box-shadow 0.12s",
              maxWidth: 240,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 24px ${COLORS.accent}22`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <AgentAvatar agent={a} size={28} variant="circle" />
            <div style={{
              fontSize: 12, fontWeight: 600, color: COLORS.text,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 160,
            }}>{a.name || "(unnamed)"}</div>
          </button>
        ))}
      </div>
    </div>
  );
}


export default AgentLibraryView;
